/**
 * Catálogo de beneficios por plan (alineado con billing.plan_entitlements en Supabase).
 * Sin dependencias de React ni de la app — importable desde Next y Expo.
 */

export type PlanCode = 'gratis' | 'emprende' | 'crece' | 'legacy' | string;

/** Campos usados para bullets dinámicos (override incluido). */
export type PlanEntitlementsLike = {
  plan_code?: string;
  display_name?: string;
  is_owner?: boolean;
  max_branches?: number | null;
  max_users?: number | null;
  max_products?: number | null;
  monthly_pos_transactions_limit?: number | null;
  monthly_online_orders_limit?: number | null;
  monthly_appointments_limit?: number | null;
  allow_employees?: boolean;
  multi_branch?: boolean;
  fiado_payment_link?: boolean;
  fiado_manual_only?: boolean;
  mp_qr_pos?: boolean;
  mp_qr_demo_only?: boolean;
  fiscal_enabled?: boolean;
  audit_enabled?: boolean;
  inteligencia_basica?: boolean;
  inteligencia_avanzada?: boolean;
  branch_count?: number | null;
  /** Dueño + empleados + invitaciones pendientes (misma regla que cupos en invitaciones). */
  user_count?: number | null;
  subscription_status?: string | null;
  subscription_expires_at?: string | null;
  pending_subscription_plan?: string | null;
  active_plan_grant?: {
    plan_code?: string;
    expires_at?: string;
    source?: string;
  } | null;
};

/** Bullets del plan «A medida» (contacto comercial; no está en el catálogo RPC). */
export const CUSTOM_PLAN_MARKETING_BULLETS = [
  'Más sucursales, usuarios o productos que Crece',
  'Límites y funciones según tu operación',
  'Onboarding y soporte directo con el equipo',
  'Precio mensual a convenir',
] as const;

/** Plan base sin costo fijo. */
export const PLAN_GRATIS_BULLETS = [
  '1 sucursal y 1 usuario',
  'Productos sin límite en el catálogo',
  'POS, stock y caja',
  'Mercado Pago QR en el POS',
  'Tienda online y pedidos',
  'Clientes y fiado manual',
  'Facturación fiscal (ARCA)',
  'Promociones',
  'Inteligencia básica',
] as const;

/** Encabezados acumulativos en tarjetas de planes. */
export const PLAN_CUMULATIVE_LEAD = {
  emprende: 'Todo lo de Gratis, con más capacidad:',
  crece: 'Todo lo de Gratis, más:',
} as const;

/** Beneficios que suma Emprende respecto de Gratis. */
export const PLAN_EMPRENDE_INCREMENTAL_BULLETS = [
  'Más ventas, pedidos y turnos por mes',
] as const;

/** Beneficios que suma Crece respecto de Gratis. */
export const PLAN_CRECE_INCREMENTAL_BULLETS = [
  'Ventas, pedidos y turnos sin límite',
  'Sucursales y usuarios sin límite',
  'Equipo: empleados y permisos por sucursal',
  'Multi-sucursal con gestión centralizada',
  'Clientes con link de cobro online',
  'Auditoría e historial ampliado',
  'Inteligencia avanzada',
  'Soporte prioritario',
] as const;

/** Bullets de marketing por plan (landing, tarjetas de precio). */
export const PLAN_MARKETING_BULLETS: Record<'gratis' | 'emprende' | 'crece' | 'legacy', string[]> = {
  gratis: [...PLAN_GRATIS_BULLETS],
  emprende: [PLAN_CUMULATIVE_LEAD.emprende, ...PLAN_EMPRENDE_INCREMENTAL_BULLETS],
  crece: [PLAN_CUMULATIVE_LEAD.crece, ...PLAN_CRECE_INCREMENTAL_BULLETS],
  legacy: [PLAN_CUMULATIVE_LEAD.crece, ...PLAN_CRECE_INCREMENTAL_BULLETS],
};

/** Línea acumulativa (sin viñeta de check en UI). */
export function isPlanCumulativeLeadBullet(text: string): boolean {
  return text === PLAN_CUMULATIVE_LEAD.emprende || text === PLAN_CUMULATIVE_LEAD.crece;
}

export function normalizePlanCode(code: string | undefined | null): 'gratis' | 'emprende' | 'crece' | 'legacy' | 'other' {
  const p = (code ?? '').trim().toLowerCase();
  if (p === 'gratis') return 'gratis';
  if (p === 'emprende') return 'emprende';
  if (p === 'crece') return 'crece';
  if (p === 'legacy') return 'legacy';
  // Compatibilidad: plan legacy esencial/completo → legacy (usuarios existentes)
  if (p === 'completo' || p === 'esencial') return 'legacy';
  return 'other';
}

export function marketingBulletsForPlan(planCode: string | undefined | null): string[] {
  const n = normalizePlanCode(planCode);
  if (n === 'other') {
    return ['Incluye los límites del plan asignado a tu organización.'];
  }
  return [...PLAN_MARKETING_BULLETS[n]];
}

/**
 * Texto de uso de sucursales. Si used > max, marca overQuota (avisar en UI).
 */
export function branchUsageFromEntitlements(row: PlanEntitlementsLike): {
  /** Texto con unidad (p. ej. caption sin KPI). */
  line: string;
  /** Valor compacto para celdas KPI (p. ej. `2/3`). */
  value: string;
  overQuota: boolean;
  /** Uso actual (para meters/barras). */
  used: number;
  /** Tope del plan; null = sin límite. */
  max: number | null;
} {
  const used = Math.max(0, row.branch_count ?? 0);
  const max = row.max_branches;
  if (max == null) {
    const value = String(used);
    return { line: `${used} sucursales`, value, overQuota: false, used, max: null };
  }
  const overQuota = used > max;
  const value = `${used}/${max}`;
  return { line: `${value} sucursales`, value, overQuota, used, max };
}

/**
 * Uso de cupo de usuarios (dueño + equipo + invitaciones pendientes).
 * Si el backend no envía `user_count`, no mostrar esta línea.
 */
export function userUsageFromEntitlements(row: PlanEntitlementsLike): {
  line: string;
  value: string;
  overQuota: boolean;
  /** Uso actual (para meters/barras). */
  used: number;
  /** Tope del plan; null = sin límite. */
  max: number | null;
} | null {
  if (row.user_count == null) return null;
  const used = Math.max(0, row.user_count);
  const max = row.max_users;
  if (max == null) {
    const value = String(used);
    return { line: `${used} usuarios`, value, overQuota: false, used, max: null };
  }
  const overQuota = used > max;
  const value = `${used}/${max}`;
  return { line: `${value} usuarios`, value, overQuota, used, max };
}

/** Bullets que reflejan el plan efectivo (números reales / overrides). */
export function entitlementsSnapshotBullets(row: PlanEntitlementsLike): string[] {
  const bullets: string[] = [];
  const maxB = row.max_branches;
  const maxU = row.max_users;
  const maxP = row.max_products;

  if (maxB != null) {
    bullets.push(`Hasta ${maxB} sucursal${maxB === 1 ? '' : 'es'}`);
  } else {
    bullets.push('Sucursales sin límite fijo en el plan');
  }

  if (maxU != null) {
    bullets.push(`Hasta ${maxU} usuario${maxU === 1 ? '' : 's'}`);
  } else {
    bullets.push('Usuarios sin límite fijo en el plan');
  }

  if (maxP != null) {
    bullets.push(`Hasta ${maxP} productos en catálogo`);
  } else {
    bullets.push('Productos sin límite en el catálogo');
  }

  if (row.allow_employees) {
    bullets.push('Equipo: empleados y permisos');
  } else {
    bullets.push('Sin módulo de empleados en este plan');
  }

  if (row.fiado_payment_link) {
    bullets.push('Clientes con link de cobro');
  } else if (row.fiado_manual_only) {
    bullets.push('Clientes (crédito manual)');
  }

  if (row.mp_qr_pos) {
    bullets.push('Mercado Pago en el POS');
  }

  if (row.multi_branch) {
    bullets.push('Gestión multi-sucursal');
  }

  if (row.fiscal_enabled) {
    bullets.push('Integración fiscal (ARCA)');
  }

  if (row.audit_enabled) {
    bullets.push('Auditoría e historial');
  }

  if (row.inteligencia_avanzada) {
    bullets.push('Inteligencia avanzada');
  } else if (row.inteligencia_basica) {
    bullets.push('Inteligencia básica');
  }

  return bullets;
}

/**
 * Viñetas para “Plan actual” en facturación: no repite topes que ya mostramos arriba como uso (n de m).
 */
export function entitlementsBillingBullets(row: PlanEntitlementsLike): string[] {
  let bullets = entitlementsSnapshotBullets(row);
  if (row.max_branches != null) {
    const maxB = row.max_branches;
    const branchCapLine =
      maxB === 1 ? `Hasta ${maxB} sucursal` : `Hasta ${maxB} sucursales`;
    bullets = bullets.filter((line) => line !== branchCapLine);
  }
  if (row.max_users != null && row.user_count != null) {
    const maxU = row.max_users;
    const userCapLine =
      maxU === 1 ? `Hasta ${maxU} usuario` : `Hasta ${maxU} usuarios`;
    bullets = bullets.filter((line) => line !== userCapLine);
  }
  return bullets;
}
